import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { createPortal } from 'react-dom';
import ReactFlow, {
  Background,
  Controls,
  Handle,
  Position,
  MarkerType,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import '@fontsource-variable/geist/wght.css';
import { Ticket, X, Check, Plus, ChevronRight, Loader2, Signpost, Package } from 'lucide-react';
import RemanejamentoEquipamentoFlow from './RemanejamentoEquipamentoFlow';
import CustomersCadastroPermissoesView from './CustomersCadastroPermissoesView';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { invalidateOsStatusInicialCache } from '../../board-chat/services/boardNovosChamadosService';
import { useCxExpToast } from '../../customer-experience/components/CxExpSonnerToast';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import '../../process-config/ConfigStatusView2.css';
import './CustomersProcessosView.css';

const API_BASE_URL = getApiBaseUrl();

/** Chaves em byId + colunas LCMY_VC_SYS_CONFIG */
const OS_STATUS_INICIAL_KEY = 'os_status_inicial';
const OS_STATUS_PRE_SERVICE_KEY = 'os_status_pre_service';
const OS_PRE_SERVICE_ENABLED_KEY = 'os_pre_service_enabled';

function normalizeOsStatusCod(raw) {
  return String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, 2);
}

function Pcs2Subblock({ title, desc, children, hideHead = false }) {
  return (
    <div className="pcs2-subblock">
      {!hideHead && (title || desc) ? (
        <div className="pcs2-subhead">
          <div className="pcs2-subhead-copy">
            {title ? <h4 className="pcs2-subtitle">{title}</h4> : null}
            {desc ? <p className="pcs2-subdesc">{desc}</p> : null}
          </div>
        </div>
      ) : null}
      {children}
    </div>
  );
}

function Pcs2Section({ icon: Icon, title, desc, children }) {
  return (
    <section className="pcs2-section">
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <Icon size={16} strokeWidth={1.75} aria-hidden />
          <h3 className="pcs2-section-title">{title}</h3>
        </div>
        <p className="pcs2-section-desc">{desc}</p>
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

function Pcs2ListItem({ icon: Icon, label, id, value, onChange, disabled }) {
  return (
    <div className={`pcs2-list-item${disabled ? ' pcs2-list-item--disabled' : ''}`}>
      <span className="pcs2-list-icon">
        <Icon size={12} strokeWidth={1.75} aria-hidden />
      </span>
      <span className="pcs2-list-label">{label}</span>
      <input
        type="text"
        id={id}
        className="pcs2-list-pin"
        maxLength={2}
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-label={label}
        autoComplete="off"
      />
      <ChevronRight className="pcs2-list-chev" aria-hidden />
    </div>
  );
}

function Pcs2SettingRow({ title, desc, children }) {
  return (
    <div className="pcs2-setting-row">
      <div className="pcs2-setting-copy">
        <span className="pcs2-setting-title">{title}</span>
        {desc ? <span className="pcs2-setting-desc">{desc}</span> : null}
      </div>
      <div className="pcs2-setting-actions">{children}</div>
    </div>
  );
}

function Pcs2Switch({ checked, onChange, ariaLabel, disabled }) {
  const inputRef = useRef(null);
  const handleChange = (e) => {
    onChange(e);
    requestAnimationFrame(() => inputRef.current?.blur());
  };
  return (
    <label className={['pcs2-switch', disabled ? 'pcs2-switch--disabled' : ''].filter(Boolean).join(' ')}>
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={handleChange}
        aria-label={ariaLabel}
      />
      <span className="pcs2-switch-pill">
        <span className="pcs2-switch-dot" />
      </span>
    </label>
  );
}

const SUP_FLOW_STEPS = [
  { id: 'sup_abertura', label: 'Pedido realizado' },
  { id: 'sup_em_separacao', label: 'Em separação' },
  { id: 'sup_separado', label: 'Separado' },
  { id: 'sup_nfe', label: 'Nota fiscal emitida' },
  { id: 'sup_roteirizado', label: 'Roteirizado' },
  { id: 'sup_saiu_entrega', label: 'Em trânsito' },
  { id: 'sup_entregue', label: 'Entregue' },
  { id: 'sup_nao_entregue', label: 'Impossibilidade de entrega' },
];

/** Nó só visual (sem persistência na API) */
const SUP_DECISION_ID = 'sup_decisao_foi_entregue';

const ROTEIRO_ANCHOR_ID = 'sup_roteirizado';
const SUP_NFE_ID = 'sup_nfe';
const SUP_SAIU_ID = 'sup_saiu_entrega';
/** Máx. 4 extras → Roteirizado #2…#5 (além do Roteirizado principal) */
const MAX_EXTRA_ROTEIRO = 4;
const SUP_ROTEIRO_EXTRA_IDS = ['sup_roteirizado_x1', 'sup_roteirizado_x2', 'sup_roteirizado_x3', 'sup_roteirizado_x4'];
/** Máx. 4 extras → Em trânsito #2…#5 (além do Em trânsito principal) */
const MAX_EXTRA_TRANSIT = 4;
const SUP_TRANSIT_EXTRA_IDS = ['sup_saiu_entrega_x1', 'sup_saiu_entrega_x2', 'sup_saiu_entrega_x3', 'sup_saiu_entrega_x4'];
/** Ordem alinhada ao backend (vcSysConfigPedController COLUMN_BY_STEP) */
const SUP_PED_STEP_IDS = [
  'sup_abertura',
  'sup_em_separacao',
  'sup_separado',
  'sup_nfe',
  'sup_roteirizado',
  ...SUP_ROTEIRO_EXTRA_IDS,
  'sup_saiu_entrega',
  ...SUP_TRANSIT_EXTRA_IDS,
  'sup_entregue',
  'sup_nao_entregue',
  'sup_cfg_operacao',
  'sup_cfg_condicao',
  'sup_cfg_vendedor',
  'sup_cfg_transportadora',
  'sup_cfg_classificacao',
  'sup_cfg_operacao_grouped',
  'sup_cfg_condicao_grouped',
  'sup_cfg_vendedor_grouped',
  'sup_cfg_transportadora_grouped',
  'sup_cfg_classificacao_grouped',
];
const SUP_CFG_OPENING_IDS = [
  'sup_cfg_operacao',
  'sup_cfg_condicao',
  'sup_cfg_vendedor',
  'sup_cfg_transportadora',
  'sup_cfg_classificacao',
];
const SUP_CFG_OPENING_GROUPED_IDS = [
  'sup_cfg_operacao_grouped',
  'sup_cfg_condicao_grouped',
  'sup_cfg_vendedor_grouped',
  'sup_cfg_transportadora_grouped',
  'sup_cfg_classificacao_grouped',
];
const SUP_CFG_ALL_OPENING_IDS = [...SUP_CFG_OPENING_IDS, ...SUP_CFG_OPENING_GROUPED_IDS];
const SUP_CFG_TAB_EQUIP = 'equip';
const SUP_CFG_TAB_GROUPED = 'grouped';
const SUP_CFG_TABS = [
  { id: SUP_CFG_TAB_EQUIP, label: 'Por equipamento', fieldIds: SUP_CFG_OPENING_IDS },
  { id: SUP_CFG_TAB_GROUPED, label: '(por site) Agrupado', fieldIds: SUP_CFG_OPENING_GROUPED_IDS },
];

const SUP_NODE_WIDTH = 180;
/** Espaço entre colunas (linhas mais longas / legível) */
const SUP_COLUMN_GAP = 56;
const SUP_FLOW_STRIDE = SUP_NODE_WIDTH + SUP_COLUMN_GAP;
const SUP_MAIN_ROW_Y = 48;
/** Espaço vertical entre “Roteirizado” principal e #2, #3… */
const SUP_ROTEIRO_STACK_DY = 132;
/** Coluna da decisão “Foi entregue?” (0-based); Entregue fica na seguinte */
const SUP_DECISION_COL = 6;
const SUP_ENTREGUE_COL = 7;
const SUP_DECISION_NODE_WIDTH = 160;
/** “Não entregue” abaixo da decisão */
const SUP_NAO_ENTREGUE_Y = SUP_MAIN_ROW_Y + 156;
const SUP_ABERTURA_CFG_ID = 'sup_abertura_cfg';
const SUP_ABERTURA_CFG_NODE_WIDTH = 460;
const SUP_ABERTURA_CFG_Y = SUP_MAIN_ROW_Y + 188;
/** Largura útil do fluxo principal (colunas 0…7 + último nó) */
const SUP_MAIN_FLOW_MIN_WIDTH = SUP_ENTREGUE_COL * SUP_FLOW_STRIDE + SUP_NODE_WIDTH + 80;

function buildEmptySupPedDrafts() {
  const o = Object.fromEntries(SUP_FLOW_STEPS.map((s) => [s.id, '']));
  [...SUP_ROTEIRO_EXTRA_IDS, ...SUP_TRANSIT_EXTRA_IDS].forEach((id) => {
    o[id] = '';
  });
  SUP_CFG_ALL_OPENING_IDS.forEach((id) => {
    o[id] = '';
  });
  return o;
}

/** Último nó da pilha: principal ou o extra com maior índice exibido. */
function supLastRoteiroId(extraCount) {
  if (extraCount <= 0) return ROTEIRO_ANCHOR_ID;
  return `sup_roteirizado_x${extraCount}`;
}

function supLastTransitId(extraCount) {
  if (extraCount <= 0) return SUP_SAIU_ID;
  return `sup_saiu_entrega_x${extraCount}`;
}

/** Reordena os extras de Em trânsito preenchidos em sequência (#2, #3…), sem lacunas vazias. */
function compactSupTransitExtras(values) {
  const filled = SUP_TRANSIT_EXTRA_IDS.map((id) => normalizeSupCodStr(values[id])).filter(Boolean);
  const out = { ...values };
  SUP_TRANSIT_EXTRA_IDS.forEach((id, i) => {
    out[id] = filled[i] || '';
  });
  return out;
}

/** Maior índice de extra com valor salvo (ex.: prefix `sup_saiu_entrega_x`). */
function inferSupExtraCount(ped, prefix, max) {
  for (let k = max; k >= 1; k -= 1) {
    const v = ped[`${prefix}${k}`];
    if (v != null && String(v).trim() !== '') return k;
  }
  return 0;
}

const supEdgeLabelStyle = { fill: '#475569', fontWeight: 600, fontSize: 11, fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif" };
const SUP_CFG_FIELD_META = {
  sup_cfg_operacao: { label: 'Operação', max: 2 },
  sup_cfg_condicao: { label: 'Condição', max: 3 },
  sup_cfg_vendedor: { label: 'Vendedor', max: 4 },
  sup_cfg_transportadora: { label: 'Transportadora', max: 4 },
  sup_cfg_classificacao: { label: 'Classificação', max: 4 },
  sup_cfg_operacao_grouped: { label: 'Operação', max: 2 },
  sup_cfg_condicao_grouped: { label: 'Condição', max: 3 },
  sup_cfg_vendedor_grouped: { label: 'Vendedor', max: 4 },
  sup_cfg_transportadora_grouped: { label: 'Transportadora', max: 4 },
  sup_cfg_classificacao_grouped: { label: 'Classificação', max: 4 },
};

/** Ramificações NFe → Roteirizados extras; Roteirizado → Em trânsito extras → decisão; decisão → Entregue | Não entregue (somente visual). */
function buildSupBranchEdges(extraRoteiroCount, extraTransitCount) {
  const edgeStyle = {
    animated: false,
    interactionWidth: 0,
    style: { stroke: '#94a3b8', strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8', width: 16, height: 16 },
  };
  const edges = [];
  const linearToSaiu = [
    'sup_abertura',
    'sup_em_separacao',
    'sup_separado',
    'sup_nfe',
    'sup_roteirizado',
    'sup_saiu_entrega',
  ];
  for (let i = 0; i < linearToSaiu.length - 1; i += 1) {
    const a = linearToSaiu[i];
    const b = linearToSaiu[i + 1];
    edges.push({
      id: `e-${a}-${b}`,
      source: a,
      target: b,
      ...(a === 'sup_abertura' ? { sourceHandle: 'main' } : {}),
      ...edgeStyle,
    });
  }
  edges.push({
    id: `e-${SUP_SAIU_ID}-${SUP_DECISION_ID}`,
    source: SUP_SAIU_ID,
    target: SUP_DECISION_ID,
    ...edgeStyle,
  });
  edges.push({
    id: `e-${SUP_DECISION_ID}-sup_entregue`,
    source: SUP_DECISION_ID,
    target: 'sup_entregue',
    sourceHandle: 'sim',
    label: 'Sim',
    labelStyle: supEdgeLabelStyle,
    labelBgStyle: { fill: '#fff', fillOpacity: 0.92 },
    ...edgeStyle,
  });
  edges.push({
    id: `e-${SUP_DECISION_ID}-sup_nao_entregue`,
    type: 'straight',
    source: SUP_DECISION_ID,
    target: 'sup_nao_entregue',
    sourceHandle: 'nao',
    targetHandle: 'top',
    label: 'Não',
    labelStyle: supEdgeLabelStyle,
    labelBgStyle: { fill: '#fff', fillOpacity: 0.92 },
    ...edgeStyle,
  });
  edges.push({
    id: 'e-sup_abertura-sup_abertura_cfg',
    type: 'straight',
    source: 'sup_abertura',
    sourceHandle: 'cfg',
    target: SUP_ABERTURA_CFG_ID,
    targetHandle: 'top',
    animated: false,
    interactionWidth: 0,
    style: { stroke: '#111827', strokeWidth: 1.7, strokeDasharray: '6 4' },
  });
  for (let k = 1; k <= extraRoteiroCount; k += 1) {
    const xid = `sup_roteirizado_x${k}`;
    edges.push({
      id: `e-${SUP_NFE_ID}-${xid}`,
      source: SUP_NFE_ID,
      target: xid,
      ...edgeStyle,
    });
    edges.push({
      id: `e-${xid}-${SUP_SAIU_ID}`,
      source: xid,
      target: SUP_SAIU_ID,
      ...edgeStyle,
    });
  }
  for (let k = 1; k <= extraTransitCount; k += 1) {
    const tid = `sup_saiu_entrega_x${k}`;
    edges.push({
      id: `e-${ROTEIRO_ANCHOR_ID}-${tid}`,
      source: ROTEIRO_ANCHOR_ID,
      target: tid,
      ...edgeStyle,
    });
    edges.push({
      id: `e-${tid}-${SUP_DECISION_ID}`,
      type: 'smoothstep',
      source: tid,
      target: SUP_DECISION_ID,
      targetHandle: 'in',
      ...edgeStyle,
    });
  }
  return edges;
}

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function isConfiguredCod(cod) {
  const u = String(cod ?? '').trim().toUpperCase();
  return u.length === 2;
}

function normalizeSupCodStr(raw) {
  return String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, 2);
}

function normalizeSupCfgAlnum(raw, maxLen) {
  return String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, maxLen);
}

function normalizeSupValueById(id, raw) {
  if (SUP_CFG_FIELD_META[id]) {
    return normalizeSupCfgAlnum(raw, SUP_CFG_FIELD_META[id].max);
  }
  return normalizeSupCodStr(raw);
}

function isValidSupValueById(id, raw) {
  const v = normalizeSupValueById(id, raw);
  if (SUP_CFG_FIELD_META[id]) {
    const req = SUP_CFG_FIELD_META[id].max;
    return v.length === 0 || v.length === req;
  }
  return v.length === 0 || v.length === 2;
}

const SupDecisionNode = memo(function SupDecisionNode() {
  return (
    <div className="cp-sup-decision nodrag nopan nowheel">
      <Handle type="target" position={Position.Left} id="in" style={{ background: '#64748b' }} />
      <div className="cp-sup-decision__shape" aria-hidden />
      <p className="cp-sup-decision__text">Foi entregue?</p>
      <Handle
        type="source"
        position={Position.Right}
        id="sim"
        style={{ top: '38%', background: '#64748b' }}
      />
      <Handle type="source" position={Position.Bottom} id="nao" style={{ background: '#64748b' }} />
    </div>
  );
});

const SupStepNode = memo(function SupStepNode({ data }) {
  const { label, draft, active, stepId, onDraft, showAddRoteiro, onAddRoteiro, addTitle, onRemove } = data;
  const addLabel = addTitle || 'Adicionar etapa Roteirizado extra';
  const removeLabel = `Retirar o status "${label}" (salve a configuração para confirmar)`;
  const topTarget = stepId === 'sup_nao_entregue';
  const cfgSource = stepId === 'sup_abertura';
  return (
    <div className={`cp-sup-node ${active ? 'cp-sup-node--on' : 'cp-sup-node--off'}`}>
      {topTarget && (
        <Handle type="target" position={Position.Top} id="top" style={{ background: '#94a3b8' }} />
      )}
      <Handle type="target" position={Position.Left} style={{ background: '#94a3b8' }} />
      <div className="cp-sup-node__head-row">
        <div className="cp-sup-node__head">{label}</div>
        {showAddRoteiro && (
          <button
            type="button"
            className="cp-sup-node__add nodrag nopan nowheel"
            title={addLabel}
            aria-label={addLabel}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onAddRoteiro?.();
            }}
          >
            <Plus size={12} strokeWidth={2.5} aria-hidden />
          </button>
        )}
        {onRemove && (
          <GoHoverTooltip text={removeLabel} className="go-hover-tip-wrap--contents">
            <button
              type="button"
              className="cp-sup-node__remove nodrag nopan nowheel"
              aria-label={removeLabel}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(stepId);
              }}
            >
              <X size={10} strokeWidth={2.75} aria-hidden />
            </button>
          </GoHoverTooltip>
        )}
        <span
          className={`cp-sup-node__status ${active ? 'cp-sup-node__status--ok' : 'cp-sup-node__status--off'}`}
          title={active ? 'Configurado' : 'Não configurado'}
          aria-label={active ? 'Etapa configurada' : 'Etapa não configurada'}
        >
          {active ? <Check size={11} strokeWidth={2.5} aria-hidden /> : <X size={11} strokeWidth={2.5} aria-hidden />}
        </span>
      </div>
      <input
        type="text"
        className="cp-sup-node__input nodrag nopan nowheel"
        maxLength={2}
        autoComplete="off"
        aria-label={`Código de status — ${label}`}
        value={draft}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onChange={(e) => {
          const v = e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 2);
          onDraft(stepId, v);
        }}
      />
      {cfgSource && <Handle type="source" position={Position.Bottom} id="cfg" style={{ background: '#94a3b8' }} />}
      <Handle type="source" position={Position.Right} id="main" style={{ background: '#94a3b8' }} />
    </div>
  );
});

const SupOpeningConfigNode = memo(function SupOpeningConfigNode({ data }) {
  const { fields, onDraft, invalidFieldIds = [], cfgTab = SUP_CFG_TAB_EQUIP, onCfgTab } = data;
  const invalidSet = useMemo(() => new Set(invalidFieldIds), [invalidFieldIds]);

  useEffect(() => {
    if (!invalidFieldIds.length || typeof onCfgTab !== 'function') return;
    const tabWithError = SUP_CFG_TABS.find((t) => t.fieldIds.some((id) => invalidSet.has(id)));
    if (tabWithError && tabWithError.id !== cfgTab) {
      onCfgTab(tabWithError.id);
    }
  }, [invalidFieldIds, invalidSet, cfgTab, onCfgTab]);

  const activeTab = SUP_CFG_TABS.find((t) => t.id === cfgTab) || SUP_CFG_TABS[0];

  return (
    <div className="cp-sup-config-node nodrag nopan nowheel">
      <Handle type="target" position={Position.Top} id="top" style={{ background: '#0f766e' }} />
      <div className="cp-sup-config-node__title">Configuração da abertura do pedido</div>
      <div className="cp-sup-config-node__tabs nodrag nopan nowheel" role="tablist" aria-label="Tipo de configuração">
        {SUP_CFG_TABS.map((t) => {
          const hasErr = t.fieldIds.some((id) => invalidSet.has(id));
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={cfgTab === t.id}
              className={`cp-sup-config-node__tab nodrag nopan nowheel${cfgTab === t.id ? ' is-active' : ''}${hasErr ? ' has-error' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                onCfgTab?.(t.id);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <div className="cp-sup-config-node__grid nodrag nopan nowheel" role="tabpanel">
        {activeTab.fieldIds.map((id) => {
          const meta = SUP_CFG_FIELD_META[id];
          const value = fields[id] ?? '';
          const partial = value.length > 0 && value.length < meta.max;
          const isInvalid = partial || invalidSet.has(id);
          const hint =
            value.length === 0
              ? `Informe ${meta.max} caracteres alfanuméricos`
              : value.length < meta.max
                ? `Faltam ${meta.max - value.length} (máx. ${meta.max} alfanuméricos)`
                : '';
          return (
            <label
              key={id}
              className={`cp-sup-config-node__field ${isInvalid ? 'cp-sup-config-node__field--invalid' : ''}`}
              title={hint || undefined}
            >
              <span>{meta.label}</span>
              <input
                type="text"
                className={`cp-sup-config-node__input nodrag nopan nowheel ${isInvalid ? 'cp-sup-config-node__input--invalid' : ''}`}
                maxLength={meta.max}
                autoComplete="off"
                spellCheck={false}
                aria-invalid={isInvalid}
                aria-label={`${meta.label}: exatamente ${meta.max} caracteres alfanuméricos`}
                value={value}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onChange={(e) => onDraft(id, normalizeSupCfgAlnum(e.target.value, meta.max))}
              />
              {isInvalid && hint ? <em className="cp-sup-config-node__hint">{hint}</em> : null}
            </label>
          );
        })}
      </div>
    </div>
  );
});

const nodeTypes = { supStep: SupStepNode, supDecision: SupDecisionNode, supOpeningConfig: SupOpeningConfigNode };

/** Colunas na linha principal (Entregue deslocado após a decisão) */
const SUP_MAIN_COL_BY_ID = {
  sup_abertura: 0,
  sup_em_separacao: 1,
  sup_separado: 2,
  sup_nfe: 3,
  sup_roteirizado: 4,
  sup_saiu_entrega: 5,
  sup_entregue: SUP_ENTREGUE_COL,
};

export default function CustomersProcessosView() {
  const [tab, setTab] = useState('os');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [byId, setById] = useState({});

  const [osDraft, setOsDraft] = useState('');
  const [osPreDraft, setOsPreDraft] = useState('');
  const [osPreEnabled, setOsPreEnabled] = useState(false);
  const [osSaving, setOsSaving] = useState(false);
  const { success: toastSuccess, error: toastError, Toaster } = useCxExpToast();

  const [supDrafts, setSupDrafts] = useState(buildEmptySupPedDrafts);
  const [supSavingAll, setSupSavingAll] = useState(false);
  const [supCfgTab, setSupCfgTab] = useState(SUP_CFG_TAB_EQUIP);
  const [supCfgInvalidIds, setSupCfgInvalidIds] = useState([]);
  /** Quantidade de nós extras após “Roteirizado” (Roteirizado #2, #3, …) — só UI */
  const [supExtraRoteiroCount, setSupExtraRoteiroCount] = useState(0);

  const onAddRoteiroExtra = useCallback(() => {
    setSupExtraRoteiroCount((n) => {
      if (n >= MAX_EXTRA_ROTEIRO) return n;
      const next = n + 1;
      const newId = `sup_roteirizado_x${next}`;
      setSupDrafts((prev) => ({ ...prev, [newId]: '' }));
      return next;
    });
  }, []);

  /** Quantidade de nós extras após “Em trânsito” (Em trânsito #2, #3, …) — só UI */
  const [supExtraTransitCount, setSupExtraTransitCount] = useState(0);

  const onAddTransitExtra = useCallback(() => {
    setSupExtraTransitCount((n) => {
      if (n >= MAX_EXTRA_TRANSIT) return n;
      const next = n + 1;
      const newId = `sup_saiu_entrega_x${next}`;
      setSupDrafts((prev) => ({ ...prev, [newId]: '' }));
      return next;
    });
  }, []);

  /** Retira um extra (Roteirizado/Em trânsito #2…#5), sobe os seguintes; ao salvar a coluna vazia vira NULL. */
  const onRemoveSupExtra = useCallback(
    (stepId) => {
      const isTransit = stepId.startsWith('sup_saiu_entrega_x');
      const ids = isTransit ? SUP_TRANSIT_EXTRA_IDS : SUP_ROTEIRO_EXTRA_IDS;
      const idx = ids.indexOf(stepId);
      if (idx < 0) return;
      const hadValue = ids
        .slice(idx)
        .some((id) => normalizeSupCodStr(supDrafts[id]) || normalizeSupCodStr(byId[id]));
      setSupDrafts((prev) => {
        const vals = ids.map((id) => prev[id] ?? '');
        vals.splice(idx, 1);
        vals.push('');
        const next = { ...prev };
        ids.forEach((id, i) => {
          next[id] = vals[i];
        });
        return next;
      });
      (isTransit ? setSupExtraTransitCount : setSupExtraRoteiroCount)((n) => Math.max(0, n - 1));
      if (hadValue) toastSuccess('Status retirado. Clique em "Salvar configuração" para confirmar.');
    },
    [supDrafts, byId, toastSuccess]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [osRes, pedRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/config/vc-sys-config/os-status-inicial`, {
          headers: authHeaders(),
        }),
        fetch(`${API_BASE_URL}/api/config/vc-sys-config/ped-status`, {
          headers: authHeaders(),
        }),
      ]);
      const osJson = await osRes.json().catch(() => ({}));
      const pedJson = await pedRes.json().catch(() => ({}));
      if (!osRes.ok || osJson.success === false) {
        throw new Error(osJson.message || `Erro ${osRes.status}`);
      }
      if (!pedRes.ok || pedJson.success === false) {
        throw new Error(pedJson.message || `Erro ${pedRes.status}`);
      }
      const osData = osJson.data && typeof osJson.data === 'object' ? osJson.data : {};
      const ped = compactSupTransitExtras(
        pedJson.data && typeof pedJson.data === 'object' ? pedJson.data : {}
      );
      const map = {};
      const osCodRaw = osData.codstatus;
      const osPreRaw = osData.preServiceStatus;
      const osPreEnabledRaw = Boolean(osData.preServiceEnabled);
      map[OS_STATUS_INICIAL_KEY] =
        osCodRaw != null && String(osCodRaw).trim() !== '' ? normalizeOsStatusCod(osCodRaw) : '';
      map[OS_STATUS_PRE_SERVICE_KEY] =
        osPreRaw != null && String(osPreRaw).trim() !== '' ? normalizeOsStatusCod(osPreRaw) : '';
      map[OS_PRE_SERVICE_ENABLED_KEY] = osPreEnabledRaw ? '1' : '0';
      SUP_FLOW_STEPS.forEach((s) => {
        const v = ped[s.id];
        map[s.id] = v != null && String(v).trim() !== '' ? normalizeSupValueById(s.id, v) : '';
      });
      [...SUP_ROTEIRO_EXTRA_IDS, ...SUP_TRANSIT_EXTRA_IDS].forEach((id) => {
        const v = ped[id];
        map[id] = v != null && String(v).trim() !== '' ? normalizeSupCodStr(v) : '';
      });
      SUP_CFG_ALL_OPENING_IDS.forEach((id) => {
        const v = ped[id];
        map[id] = v != null && String(v).trim() !== '' ? normalizeSupValueById(id, v) : '';
      });
      setById(map);
      setOsDraft(normalizeOsStatusCod(map[OS_STATUS_INICIAL_KEY]));
      setOsPreDraft(normalizeOsStatusCod(map[OS_STATUS_PRE_SERVICE_KEY]));
      setOsPreEnabled(osPreEnabledRaw);
      let inferredExtra = 0;
      for (let k = MAX_EXTRA_ROTEIRO; k >= 1; k -= 1) {
        const id = `sup_roteirizado_x${k}`;
        const v = ped[id];
        if (v != null && String(v).trim() !== '') {
          inferredExtra = k;
          break;
        }
      }
      setSupExtraRoteiroCount(inferredExtra);
      setSupExtraTransitCount(inferSupExtraCount(ped, 'sup_saiu_entrega_x', MAX_EXTRA_TRANSIT));
      setSupDrafts((prev) => {
        const next = { ...prev };
        SUP_PED_STEP_IDS.forEach((id) => {
          const v = ped[id];
          next[id] = v != null && String(v).trim() !== '' ? normalizeSupValueById(id, v) : '';
        });
        return next;
      });
      setSupCfgInvalidIds([]);
    } catch (e) {
      setLoadError(e.message || 'Falha ao carregar');
      setById({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const osSaved = String(byId[OS_STATUS_INICIAL_KEY] || '');
  const osPreSaved = String(byId[OS_STATUS_PRE_SERVICE_KEY] || '');
  const osPreEnabledSaved = String(byId[OS_PRE_SERVICE_ENABLED_KEY] || '0') === '1';
  const osDraftNorm = normalizeOsStatusCod(osDraft);
  const osPreDraftNorm = normalizeOsStatusCod(osPreDraft);
  const osDirty =
    osDraftNorm !== osSaved
    || osPreDraftNorm !== osPreSaved
    || osPreEnabled !== osPreEnabledSaved;

  const saveOs = useCallback(async () => {
    const cod = normalizeOsStatusCod(osDraft);
    const pre = normalizeOsStatusCod(osPreDraft);
    if (cod.length !== 2) {
      toastError('Código do status: informe exatamente 2 caracteres.');
      return;
    }
    if (osPreEnabled) {
      if (pre.length !== 2) {
        toastError('Status de pré-atendimento: informe exatamente 2 caracteres.');
        return;
      }
      if (pre === cod) {
        toastError('Pré-atendimento e código do status devem ser diferentes.');
        return;
      }
    }
    setOsSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/config/vc-sys-config/os-status-inicial`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          codstatus: cod,
          preServiceStatus: pre,
          preServiceEnabled: osPreEnabled,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        throw new Error(json.message || `Erro ${res.status}`);
      }
      setById((prev) => ({
        ...prev,
        [OS_STATUS_INICIAL_KEY]: cod,
        [OS_STATUS_PRE_SERVICE_KEY]: pre,
        [OS_PRE_SERVICE_ENABLED_KEY]: osPreEnabled ? '1' : '0',
      }));
      setOsDraft(cod);
      setOsPreDraft(pre);
      setOsPreEnabled(osPreEnabled);
      invalidateOsStatusInicialCache();
      toastSuccess('Configuração salva com sucesso!');
    } catch (e) {
      toastError(e.message || 'Erro ao salvar');
    } finally {
      setOsSaving(false);
    }
  }, [osDraft, osPreDraft, osPreEnabled, toastError, toastSuccess]);

  const onSupCfgTab = useCallback((tabId) => {
    setSupCfgTab(tabId);
  }, []);

  const onSupDraft = useCallback((stepId, v) => {
    setSupDrafts((prev) => ({ ...prev, [stepId]: v }));
    if (SUP_CFG_FIELD_META[stepId]) {
      setSupCfgInvalidIds((prev) => prev.filter((id) => id !== stepId));
    }
  }, []);

  const supCfgPartialIds = useMemo(
    () =>
      SUP_CFG_ALL_OPENING_IDS.filter((id) => {
        const v = normalizeSupValueById(id, supDrafts[id]);
        const max = SUP_CFG_FIELD_META[id].max;
        return v.length > 0 && v.length !== max;
      }),
    [supDrafts]
  );

  const supDirty = useMemo(
    () =>
      SUP_PED_STEP_IDS.some((id) => {
        const draft = normalizeSupValueById(id, supDrafts[id]);
        const saved = normalizeSupValueById(id, byId[id]);
        return draft !== saved;
      }),
    [supDrafts, byId]
  );

  const saveAllSup = useCallback(async () => {
    if (supCfgPartialIds.length > 0) {
      setSupCfgInvalidIds(supCfgPartialIds);
      toastError('Complete ou limpe os campos de configuração com preenchimento parcial.');
      return;
    }
    const payload = {};
    const drafts = compactSupTransitExtras(supDrafts);
    for (const id of SUP_PED_STEP_IDS) {
      const cod = normalizeSupValueById(id, drafts[id]);
      if (!isValidSupValueById(id, cod)) {
        toastError('Preencha vazio ou a quantidade exata de dígitos/caracteres de cada campo.');
        return;
      }
      payload[id] = cod;
    }
    setSupSavingAll(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/config/vc-sys-config/ped-status`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        throw new Error(json.message || `Erro ${res.status}`);
      }
      const data = json.data && typeof json.data === 'object' ? json.data : {};
      setById((prev) => {
        const next = { ...prev };
        SUP_PED_STEP_IDS.forEach((id) => {
          const v = data[id];
          next[id] = v != null && String(v).trim() !== '' ? normalizeSupValueById(id, v) : '';
        });
        return next;
      });
      setSupDrafts((prev) => {
        const n = { ...prev };
        SUP_PED_STEP_IDS.forEach((id) => {
          const v = data[id];
          n[id] = v != null && String(v).trim() !== '' ? normalizeSupValueById(id, v) : '';
        });
        return n;
      });
      setSupCfgInvalidIds([]);
      setSupExtraTransitCount(inferSupExtraCount(data, 'sup_saiu_entrega_x', MAX_EXTRA_TRANSIT));
      toastSuccess('Fluxo salvo.');
    } catch (e) {
      toastError(e.message || 'Erro ao salvar');
    } finally {
      setSupSavingAll(false);
    }
  }, [supDrafts, supCfgPartialIds, toastError, toastSuccess]);

  const supAllStepsValid = useMemo(
    () =>
      SUP_PED_STEP_IDS.every((id) => {
        return isValidSupValueById(id, supDrafts[id]);
      }),
    [supDrafts]
  );
  const supSaveBlockedReason = useMemo(() => {
    if (supSavingAll) return 'Salvando em andamento.';
    if (!supDirty) return 'Nenhuma alteração para salvar.';
    if (!supAllStepsValid) {
      return 'Há etapas com quantidade de caracteres inválida.';
    }
    if (supCfgPartialIds.length > 0) {
      const partialLabels = supCfgPartialIds
        .map((id) => SUP_CFG_FIELD_META[id]?.label)
        .filter(Boolean)
        .join(', ');
      return `Campos de configuração incompletos: ${partialLabels}.`;
    }
    return '';
  }, [supSavingAll, supDirty, supAllStepsValid, supCfgPartialIds]);
  const supSaveDisabled =
    supSavingAll || !supDirty || !supAllStepsValid || supCfgPartialIds.length > 0;

  const supLastRoteiroStepId = useMemo(
    () => supLastRoteiroId(supExtraRoteiroCount),
    [supExtraRoteiroCount]
  );

  const supShowAddRoteiro = useCallback(
    (stepId) =>
      stepId === supLastRoteiroStepId && supExtraRoteiroCount < MAX_EXTRA_ROTEIRO,
    [supLastRoteiroStepId, supExtraRoteiroCount]
  );

  const supLastTransitStepId = useMemo(
    () => supLastTransitId(supExtraTransitCount),
    [supExtraTransitCount]
  );

  /** Só libera o próximo Em trânsito quando o último da pilha estiver preenchido. */
  const supShowAddTransit = useCallback(
    (stepId) =>
      stepId === supLastTransitStepId
      && supExtraTransitCount < MAX_EXTRA_TRANSIT
      && isConfiguredCod(supDrafts[stepId]),
    [supLastTransitStepId, supExtraTransitCount, supDrafts]
  );

  const supFlowEdges = useMemo(
    () => buildSupBranchEdges(supExtraRoteiroCount, supExtraTransitCount),
    [supExtraRoteiroCount, supExtraTransitCount]
  );

  const [supNodes, setSupNodes, onSupNodesChange] = useNodesState([]);
  const [supEdges, setSupEdges, onSupEdgesChange] = useEdgesState([]);
  const supFlowInstanceRef = useRef(null);

  const fitSupFlowView = useCallback(() => {
    const inst = supFlowInstanceRef.current;
    if (!inst) return;
    const opts = { padding: 0.08, maxZoom: 0.95, minZoom: 0.28, duration: 0 };
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        inst.fitView(opts);
        const vp = inst.getViewport?.();
        if (vp && Number.isFinite(vp.zoom)) {
          inst.setViewport(
            { x: vp.x, y: vp.y, zoom: vp.zoom * 0.85 },
            { duration: 0 }
          );
        }
      });
    });
  }, []);

  useEffect(() => {
    setSupEdges(supFlowEdges);
  }, [supFlowEdges, setSupEdges]);

  useEffect(() => {
    const mainNodes = [];
    for (const s of SUP_FLOW_STEPS) {
      if (s.id === 'sup_nao_entregue') {
        mainNodes.push({
          id: s.id,
          type: 'supStep',
          position: {
            x: SUP_DECISION_COL * SUP_FLOW_STRIDE,
            y: SUP_NAO_ENTREGUE_Y,
          },
          data: {
            label: s.label,
            stepId: s.id,
            draft: supDrafts[s.id] ?? '',
            active: isConfiguredCod(supDrafts[s.id] ?? byId[s.id]),
            onDraft: onSupDraft,
            showAddRoteiro: false,
            onAddRoteiro: onAddRoteiroExtra,
          },
        });
        continue;
      }
      const col = SUP_MAIN_COL_BY_ID[s.id];
      const isTransit = s.id === SUP_SAIU_ID;
      mainNodes.push({
        id: s.id,
        type: 'supStep',
        position: { x: col * SUP_FLOW_STRIDE, y: SUP_MAIN_ROW_Y },
        data: {
          label: s.label,
          stepId: s.id,
          draft: supDrafts[s.id] ?? '',
          active: isConfiguredCod(supDrafts[s.id] ?? byId[s.id]),
          onDraft: onSupDraft,
          showAddRoteiro: isTransit ? supShowAddTransit(s.id) : supShowAddRoteiro(s.id),
          onAddRoteiro: isTransit ? onAddTransitExtra : onAddRoteiroExtra,
          ...(isTransit ? { addTitle: 'Adicionar etapa Em trânsito extra' } : {}),
        },
      });
    }
    mainNodes.push({
      id: SUP_DECISION_ID,
      type: 'supDecision',
      position: {
        x: SUP_DECISION_COL * SUP_FLOW_STRIDE + (SUP_NODE_WIDTH - SUP_DECISION_NODE_WIDTH) / 2,
        y: SUP_MAIN_ROW_Y + 6,
      },
      data: {},
    });
    mainNodes.push({
      id: SUP_ABERTURA_CFG_ID,
      type: 'supOpeningConfig',
      position: {
        x: (SUP_NODE_WIDTH - SUP_ABERTURA_CFG_NODE_WIDTH) / 2,
        y: SUP_ABERTURA_CFG_Y,
      },
      data: {
        fields: supDrafts,
        onDraft: onSupDraft,
        invalidFieldIds: supCfgInvalidIds,
        cfgTab: supCfgTab,
        onCfgTab: onSupCfgTab,
      },
    });
    const roteiroColX = 4 * SUP_FLOW_STRIDE;
    const extraNodes = Array.from({ length: supExtraRoteiroCount }, (_, i) => {
      const k = i + 1;
      const id = `sup_roteirizado_x${k}`;
      return {
        id,
        type: 'supStep',
        position: {
          x: roteiroColX,
          y: SUP_MAIN_ROW_Y + k * SUP_ROTEIRO_STACK_DY,
        },
        data: {
          label: `Roteirizado #${k + 1}`,
          stepId: id,
          draft: supDrafts[id] ?? '',
          active: isConfiguredCod(supDrafts[id] ?? byId[id]),
          onDraft: onSupDraft,
          showAddRoteiro: supShowAddRoteiro(id),
          onAddRoteiro: onAddRoteiroExtra,
          onRemove: onRemoveSupExtra,
        },
      };
    });
    const transitColX = SUP_MAIN_COL_BY_ID[SUP_SAIU_ID] * SUP_FLOW_STRIDE;
    const transitNodes = Array.from({ length: supExtraTransitCount }, (_, i) => {
      const k = i + 1;
      const id = `sup_saiu_entrega_x${k}`;
      return {
        id,
        type: 'supStep',
        position: {
          x: transitColX,
          y: SUP_MAIN_ROW_Y + k * SUP_ROTEIRO_STACK_DY,
        },
        data: {
          label: `Em trânsito #${k + 1}`,
          stepId: id,
          draft: supDrafts[id] ?? '',
          active: isConfiguredCod(supDrafts[id] ?? byId[id]),
          onDraft: onSupDraft,
          showAddRoteiro: supShowAddTransit(id),
          onAddRoteiro: onAddTransitExtra,
          addTitle: 'Adicionar etapa Em trânsito extra',
          onRemove: onRemoveSupExtra,
        },
      };
    });
    setSupNodes([...mainNodes, ...extraNodes, ...transitNodes]);
  }, [
    supExtraRoteiroCount,
    supExtraTransitCount,
    supDrafts,
    byId,
    onSupDraft,
    onAddRoteiroExtra,
    onAddTransitExtra,
    onRemoveSupExtra,
    supShowAddRoteiro,
    supShowAddTransit,
    supCfgInvalidIds,
    supCfgTab,
    onSupCfgTab,
    setSupNodes,
  ]);

  /** Enquadra só ao abrir a aba; adicionar etapas extras não move a visualização. */
  const supFlowFittedRef = useRef(false);
  useEffect(() => {
    if (tab !== 'sup') {
      supFlowFittedRef.current = false;
      return;
    }
    if (supNodes.length === 0 || supFlowFittedRef.current) return;
    supFlowFittedRef.current = true;
    fitSupFlowView();
  }, [tab, supNodes.length, fitSupFlowView]);

  if (loading) {
    return (
      <div className="pcs2-page cp-processos-page">
        <section className="pcs2-section cp-processos-section">
          <div className="pcs2-section-aside">
            <div className="pcs2-section-heading">
              <Signpost size={16} strokeWidth={1.75} aria-hidden />
              <h3 className="pcs2-section-title">Processos</h3>
            </div>
          </div>
          <div className="cp-processos-surface">
            <ProcessConfigSkeleton variant="listBody" ariaLabel="Carregando processos" />
          </div>
        </section>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="pcs2-page cp-processos-page">
        <section className="pcs2-section cp-processos-section">
          <div className="pcs2-section-aside">
            <div className="pcs2-section-heading">
              <Signpost size={16} strokeWidth={1.75} aria-hidden />
              <h3 className="pcs2-section-title">Processos</h3>
            </div>
          </div>
          <div className="cp-processos-surface">
            <p className="cp-msg cp-msg--err">{loadError}</p>
            <button type="button" className="cp-btn cp-btn--primary" onClick={load}>
              Tentar novamente
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div
      className={`pcs2-page cp-processos-page${
        tab === 'rem' || tab === 'cadastro' ? ' cp-processos-page--rem' : ''
      }`}
    >
      <section className="pcs2-section cp-processos-section">
        <div className="pcs2-section-aside">
          <div className="pcs2-section-heading">
            <Signpost size={16} strokeWidth={1.75} aria-hidden />
            <h3 className="pcs2-section-title">Processos</h3>
          </div>
        </div>

        <div className="cp-processos-segment-bar">
          <div className="perm-segment cp-processos-segment" role="tablist" aria-label="Seções de processos">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'os'}
              className="perm-segment-btn"
              onClick={() => setTab('os')}
            >
              Ordem de serviço
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'sup'}
              className="perm-segment-btn"
              onClick={() => setTab('sup')}
            >
              Pedido de suprimentos
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'rem'}
              className="perm-segment-btn"
              onClick={() => setTab('rem')}
            >
              Remanejamento de equipamento
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'cadastro'}
              className="perm-segment-btn"
              onClick={() => setTab('cadastro')}
            >
              Cadastro de usuários
            </button>
          </div>
        </div>

        <div
          className={`cp-processos-surface${tab === 'rem' ? ' cp-processos-surface--rem' : ''}${
            tab === 'cadastro' ? ' cp-processos-surface--cadastro' : ''
          }`}
        >
          {tab === 'cadastro' && <CustomersCadastroPermissoesView />}
          {tab === 'os' && (
            <div className="pcs2-page cp-os-pcs2">
              <Pcs2Section
                icon={Ticket}
                title="Ordem de serviço"
                desc="Configuração do status inicial ao abrir chamado no portal"
              >
                <Pcs2Subblock
                  title="Status de abertura do chamado"
                  desc="Durante a abertura do chamado, defina o código de status (2 caracteres) que a ordem de serviço passará a assumir"
                >
                  <div className="pcs2-card">
                    <div className="pcs2-list">
                      <Pcs2ListItem
                        id="cp-os-status-inicial"
                        icon={Ticket}
                        label="Código do status"
                        value={osDraft}
                        onChange={(e) => {
                          setOsDraft(normalizeOsStatusCod(e.target.value));
                        }}
                        disabled={osSaving}
                      />
                    </div>
                  </div>
                </Pcs2Subblock>

                <Pcs2Subblock hideHead>
                  <div className="pcs2-card pcs2-card--flat pcs2-card--spaced">
                    <Pcs2SettingRow
                      title="Pré atendimento"
                      desc="Ativa o processo de pré-atendimento previamente à geração da ordem de serviço."
                    >
                      <input
                        type="text"
                        id="cp-os-status-pre-service"
                        className="pcs2-list-pin"
                        maxLength={2}
                        value={osPreDraft}
                        onChange={(e) => {
                          setOsPreDraft(normalizeOsStatusCod(e.target.value));
                        }}
                        disabled={osSaving || !osPreEnabled}
                        aria-label="Status de pré-atendimento"
                        autoComplete="off"
                      />
                      <Pcs2Switch
                        checked={osPreEnabled}
                        onChange={(e) => setOsPreEnabled(e.target.checked)}
                        disabled={osSaving}
                        ariaLabel="Pré atendimento"
                      />
                    </Pcs2SettingRow>
                  </div>
                </Pcs2Subblock>
              </Pcs2Section>

              {Toaster}

              {typeof document !== 'undefined'
                ? createPortal(
                    <button
                      type="button"
                      className="pcs2-save-fab"
                      onClick={saveOs}
                      disabled={osSaving || !osDirty}
                      aria-label="Salvar configuração"
                    >
                      {osSaving ? <Loader2 size={14} className="pcs2-spin" aria-hidden /> : null}
                      {osSaving ? 'Salvando...' : 'Salvar configuração'}
                    </button>,
                    document.body
                  )
                : null}
            </div>
          )}

          {tab === 'rem' && <RemanejamentoEquipamentoFlow />}

          {tab === 'sup' && (
            <div className="pcs2-page cp-sup-pcs2">
              <Pcs2Section
                icon={Package}
                title="Fluxo do pedido de suprimentos"
                desc="Configure o processo de pedido de suprimentos"
              >
                <div className="cp-flow-wrap">
                  <div
                    className="cp-flow-inner"
                    style={{
                      minWidth: Math.max(1512, SUP_MAIN_FLOW_MIN_WIDTH),
                    }}
                  >
                    <ReactFlow
                      nodes={supNodes}
                      edges={supEdges}
                      onNodesChange={onSupNodesChange}
                      onEdgesChange={onSupEdgesChange}
                      nodeTypes={nodeTypes}
                      defaultEdgeOptions={{ interactionWidth: 0 }}
                      nodesDraggable={false}
                      nodesConnectable={false}
                      nodesFocusable={false}
                      elementsSelectable={false}
                      selectNodesOnDrag={false}
                      edgesFocusable={false}
                      panOnScroll
                      zoomOnScroll={false}
                      preventScrolling={false}
                      onInit={(inst) => {
                        supFlowInstanceRef.current = inst;
                        requestAnimationFrame(() => {
                          requestAnimationFrame(() => {
                            inst.fitView({ padding: 0.08, maxZoom: 0.95, minZoom: 0.28, duration: 0 });
                            const vp = inst.getViewport?.();
                            if (vp && Number.isFinite(vp.zoom)) {
                              inst.setViewport(
                                { x: vp.x, y: vp.y, zoom: vp.zoom * 0.85 },
                                { duration: 0 }
                              );
                            }
                          });
                        });
                      }}
                      minZoom={0.28}
                      maxZoom={1.25}
                      proOptions={{ hideAttribution: true }}
                    >
                      <Background gap={16} color="#e2e8f0" />
                      <Controls showInteractive={false} />
                    </ReactFlow>
                  </div>
                </div>
              </Pcs2Section>

              {Toaster}

              {typeof document !== 'undefined'
                ? createPortal(
                    <button
                      type="button"
                      className="pcs2-save-fab"
                      onClick={saveAllSup}
                      disabled={supSaveDisabled}
                      title={supSaveDisabled ? supSaveBlockedReason : undefined}
                      aria-label="Salvar configuração"
                    >
                      {supSavingAll ? <Loader2 size={14} className="pcs2-spin" aria-hidden /> : null}
                      {supSavingAll ? 'Salvando...' : 'Salvar configuração'}
                    </button>,
                    document.body
                  )
                : null}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
